// WS-8 P4 — Facturación · Facturación por Q (vista 4 de 4).
//
// Réplica del Excel "_MULTIEMPAQUES Qx": la OP se factura UNA vez, completa,
// en el Q de su CIERRE (conciliación manual, o última producción validada si
// terminó automática). Se factura SOLO producción VALIDADA ✓ × tarifa del SKU
// (C10); el recargo dominical ×1.8 se cobra únicamente si el coordinador
// activó el switch de esa OP (el cliente solicitó el domingo).
//
// Estructura:
//   - Header + selector de período estándar (default: Este mes)
//   - KPIs (total a facturar, unidades ✓, recargo dominical, acumulado año)
//   - Cuadro resumen por área × Q (réplica del CUADRO RESUMEN del Excel)
//   - Tabla por Q → filas por OP con switch Dominical

import { Fragment, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import type {
  FacturacionQFila,
  FacturacionQQ,
  FacturacionQResumenArea,
  FacturacionQTiendas,
} from 'schemas';
import { Button } from '../../components/ui/button';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useFacturacionQ, useSetDominical } from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { PeriodoSelector, usePeriodoRango } from './factPeriodo';
import { exportarFacturacionQ } from './factQExport';

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('es-CO', { maximumFractionDigits: 0 });
const fmtCop = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `$${Math.round(n).toLocaleString('es-CO')}`;
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `${n.toLocaleString('es-CO', { maximumFractionDigits: 1 })}%`;

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const fmtDia = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const [, m, d] = iso.split('-');
  return `${Number(d)} ${MESES[Number(m) - 1]}`;
};

/** 'YYYY-MM-Q2' + rango → 'Q2 · 16–22 jul 2026'. */
function tituloQ(q: FacturacionQQ): string {
  const [ano, mes, qn] = q.q_key.split('-');
  const d1 = Number(q.desde.slice(8));
  const d2 = Number(q.hasta.slice(8));
  return `${qn} · ${d1}–${d2} ${MESES[Number(mes) - 1]} ${ano}`;
}

export function FacturacionQPage() {
  const { state, signOut } = useAuth();
  const { periodo, setPeriodo, rango, setRango, rangoValido, rangoEfectivo } =
    usePeriodoRango('mes'); // los cortes de Q viven dentro del mes
  const [clienteId, setClienteId] = useState('');

  const { data, isLoading, isError, error, isFetching } = useFacturacionQ({
    desde: rangoEfectivo.desde,
    hasta: rangoEfectivo.hasta,
    cliente_id: clienteId || undefined,
  });
  // Acumulado del AÑO (como el CUADRO RESUMEN acumulado del cliente).
  const anio = new Date().getFullYear();
  const anual = useFacturacionQ({
    desde: `${anio}-01-01`,
    hasta: `${anio}-12-31`,
    cliente_id: clienteId || undefined,
  });

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  // Empresa que PRESTA el servicio (encabeza el Excel que se le manda al
  // cliente). Sale del tenant activo del JWT — nunca de un literal.
  const tenantNombre =
    state.claims.tenants_disponibles.find((t) => t.id === state.claims.tenant_id_activo)?.nombre ??
    '';

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando facturación…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Facturación por Q</h1>
            <p className="text-sm text-fg-tertiary">
              Cada OP se factura una vez, en el Q de su cierre (cortes 1-15 · 16-22 · 23-fin). Solo
              producción validada ✓ × tarifa del SKU.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={data.filas.length === 0 && data.tiendas.filas.length === 0}
              title={
                data.filas.length === 0 && data.tiendas.filas.length === 0
                  ? 'No hay OPs terminadas ni corridas de tienda en el período'
                  : 'Descargar el Excel "Servicios prestados" (armado + tiendas — sin emergentes ni standby)'
              }
              onClick={() =>
                exportarFacturacionQ(
                  data,
                  clienteId
                    ? (data.clientes.find((c) => c.id === clienteId)?.nombre ?? 'cliente')
                    : 'todos',
                  tenantNombre,
                )
              }
            >
              Exportar Excel
            </Button>
            <PeriodoSelector
              periodo={periodo}
              onPeriodo={setPeriodo}
              rango={rango}
              onRango={setRango}
              rangoValido={rangoValido}
            />
          </div>
        </header>

        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <KpiCard
            label="Total a facturar"
            hint="Armado (OPs terminadas) + reempaque de tiendas (unidades + stickers/termos cobrados adicional)."
            value={fmtCop(data.totales.valor_total + data.tiendas.total.valor_total)}
            sub={
              data.tiendas.total.valor_total > 0
                ? `Armado ${fmtCop(data.totales.valor_total)} · Tiendas ${fmtCop(data.tiendas.total.valor_total)}`
                : `${fmt(data.totales.ops)} OP(s) en el período`
            }
          />
          <KpiCard
            label="Unidades validadas ✓"
            value={fmt(data.totales.unidades_validadas)}
            sub="Solo producción validada por supervisor"
          />
          <KpiCard
            label="Recargo dominical"
            hint="Domingos de OPs con el switch Dominical activo se facturan ×1.8."
            value={fmtCop(data.totales.valor_dominical)}
            sub={
              data.totales.valor_dominical > 0 ? 'Incluido en el total' : 'Sin recargo este período'
            }
          />
          <KpiCard
            label={`Acumulado año ${anio}`}
            value={fmtCop(
              anual.data
                ? anual.data.totales.valor_total + anual.data.tiendas.total.valor_total
                : undefined,
            )}
            sub={`${fmt(anual.data?.totales.unidades_validadas)} unidades ✓`}
          />
        </div>

        {data.totales.filas_sin_precio + data.tiendas.total.filas_sin_precio > 0 ? (
          <div className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-fg-secondary">
            {data.totales.filas_sin_precio + data.tiendas.total.filas_sin_precio} grupo(s) sin
            tarifa vigente para su SKU — facturan en $0. Cargue el precio en Configuración → SKUs y
            capacidades.
          </div>
        ) : null}

        {/* DN4: OPs terminadas con bloques sin validar — retenidas fuera del corte. */}
        {data.pendientes_validacion && data.pendientes_validacion.ops > 0 ? (
          <div className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-fg-secondary">
            <p>
              <span className="font-medium text-fg">
                {data.pendientes_validacion.ops} OP(s) terminada(s) pendiente(s) de validación
              </span>{' '}
              — {fmt(data.pendientes_validacion.unidades_pendientes)} unidades sin ✓ retienen{' '}
              {fmtCop(data.pendientes_validacion.valor_retenido_estimado)} fuera del corte. Entran
              completas cuando supervisión valide todos sus bloques (regla: terminada = capturada ≥
              programada o cierre manual; facturable = terminada sin bloques pendientes).
            </p>
            <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-fg-tertiary">
              {data.pendientes_validacion.filas.slice(0, 8).map((p) => (
                <li key={`${p.numero_op}-${p.sku_codigo_cliente}`} className="tabular-nums">
                  OP {p.numero_op} · {p.sku_codigo_cliente} · {fmt(p.cantidad_pendiente)} sin ✓ de{' '}
                  {fmt(p.cantidad_capturada)}
                </li>
              ))}
              {data.pendientes_validacion.filas.length > 8 ? (
                <li>y {data.pendientes_validacion.filas.length - 8} más</li>
              ) : null}
            </ul>
          </div>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fq-cliente">Cliente</Label>
            <Select
              id="fq-cliente"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-44"
            >
              <option value="">Todos</option>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          {isFetching ? <Spinner className="mb-2 size-4" /> : null}
        </div>

        {data.qs.length > 0 ? <CuadroResumen qs={data.qs} areas={data.resumen_areas} /> : null}

        <TablaFacturacion filas={data.filas} qs={data.qs} />

        <TablaTiendas tiendas={data.tiendas} />

        <footer className="text-center text-[11px] text-fg-tertiary">
          Facturación por Q · una OP factura en el Q de su cierre (o de su última producción
          validada si terminó automática) · dominical ×1.8 solo si el cliente solicitó el domingo ·
          tiendas: unidades al precio del SKU + stickers/termos cobrados adicional, en el corte de
          su producción (v1 sin dominical) · sin servicios emergentes ni standby
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// Cuadro resumen por área × Q (réplica del CUADRO RESUMEN del Excel)
// =============================================================================

function CuadroResumen({ qs, areas }: { qs: FacturacionQQ[]; areas: FacturacionQResumenArea[] }) {
  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
      {qs.map((q) => {
        const filasQ = areas.filter((a) => a.q_key === q.q_key);
        return (
          <div key={q.q_key} className="rounded border border-border p-3">
            <h3 className="mb-2 flex items-baseline justify-between text-xs font-medium uppercase tracking-wider text-fg-tertiary">
              <span>{tituloQ(q)}</span>
              <span className="text-sm normal-case tracking-normal text-fg">
                {fmtCop(q.valor_total)}
              </span>
            </h3>
            <table className="w-full text-sm">
              <tbody>
                {filasQ.map((a) => (
                  <tr key={a.area_id} className="border-t border-border/60">
                    <td className="py-1 pr-2 text-fg-secondary">{a.area_nombre}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-fg-tertiary">
                      {fmt(a.unidades_validadas)}
                    </td>
                    <td className="py-1 text-right tabular-nums">{fmtCop(a.valor_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Switch Dominical (por OP) — visible solo con producción validada de domingo
// =============================================================================

function DominicalSwitch({ fila }: { fila: FacturacionQFila }) {
  const mutation = useSetDominical();
  const activo = fila.dominical_aplica;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      disabled={mutation.isPending}
      title={
        activo
          ? 'El cliente solicitó el domingo — se factura ×1.8. Click para quitar el recargo.'
          : 'Domingo por retraso propio — tarifa normal. Click si el cliente solicitó el domingo (×1.8).'
      }
      onClick={() =>
        mutation.mutate({
          cliente_id: fila.cliente_id,
          planta_id: fila.planta_id,
          numero_op: fila.numero_op,
          sku_id: fila.sku_id,
          dominical_aplica: !activo,
        })
      }
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 ${
        activo ? 'border-accent bg-accent' : 'border-border-strong bg-surface-overlay'
      }`}
    >
      <span
        className={`inline-block size-3.5 rounded-full bg-surface-elevated shadow transition-transform ${
          activo ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// =============================================================================
// Tabla por Q → filas por OP
// =============================================================================

// =============================================================================
// Tiendas — reempaque facturable: unidades × precio del SKU + insumos cobrados
// (stickers/termos × tarifa). Cada corrida factura en el corte de su producción
// (no hay conciliación de tiendas). v1 sin recargo dominical.
// =============================================================================

function TablaTiendas({ tiendas }: { tiendas: FacturacionQTiendas }) {
  if (tiendas.filas.length === 0) return null;
  const qKeys = [...new Set(tiendas.filas.map((f) => f.q_key))].sort();
  return (
    <section className="flex flex-col gap-2">
      <div>
        <h2 className="text-sm font-medium text-fg">Tiendas (reempaque)</h2>
        <p className="text-[11px] text-fg-tertiary">
          Unidades al precio del SKU + stickers y termos cobrados adicional · factura en el corte de
          su producción · v1 sin recargo dominical
        </p>
      </div>
      <div className="overflow-auto rounded border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-surface-overlay text-xs text-fg-tertiary">
            <tr>
              <th className="px-3 py-2 text-left">Producción</th>
              <th className="px-3 py-2 text-left">Orden</th>
              <th className="px-3 py-2 text-left">SKU</th>
              <th className="px-3 py-2 text-right">Unidades</th>
              <th className="px-3 py-2 text-right">Stickers</th>
              <th className="px-3 py-2 text-right">Termos</th>
              <th className="px-3 py-2 text-right">Tarifa u.</th>
              <th className="px-3 py-2 text-right">Insumos</th>
              <th className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {qKeys.map((qk) => {
              const filasQ = tiendas.filas.filter((f) => f.q_key === qk);
              const totalQ = filasQ.reduce((s, f) => s + f.valor_total, 0);
              const undsQ = filasQ.reduce((s, f) => s + f.cantidad_unidades, 0);
              return (
                <Fragment key={qk}>
                  <tr className="border-t-2 border-border-strong bg-surface-overlay/60 font-medium">
                    <td className="px-3 py-1.5 text-fg-secondary" colSpan={3}>
                      {qk.split('-')[2]} · {qk.slice(0, 7)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(undsQ)}</td>
                    <td className="px-3 py-1.5" colSpan={4} />
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmtCop(totalQ)}</td>
                  </tr>
                  {filasQ.map((f) => (
                    <tr
                      key={`${f.q_key}-${f.orden_produccion}-${f.sku_id}-${f.planta_id}`}
                      className="border-t border-border/60"
                    >
                      <td
                        className="px-3 py-1.5 whitespace-nowrap text-fg-secondary"
                        title={`${f.planta_nombre} · producción ${fmtDia(f.fecha_inicio)} → ${fmtDia(f.fecha_fin)}`}
                      >
                        {fmtDia(f.fecha_inicio)}
                        {f.fecha_fin !== f.fecha_inicio ? `–${fmtDia(f.fecha_fin)}` : ''}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{f.orden_produccion || '—'}</td>
                      <td className="max-w-64 truncate px-3 py-1.5" title={f.sku_descripcion}>
                        <span className="tabular-nums text-fg-secondary">
                          {f.sku_codigo_cliente}
                        </span>{' '}
                        {f.sku_descripcion}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {fmt(f.cantidad_unidades)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {f.cantidad_stickers > 0 ? fmt(f.cantidad_stickers) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {f.cantidad_termos > 0 ? fmt(f.cantidad_termos) : '—'}
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right tabular-nums ${
                          f.sin_precio ? 'text-warn' : ''
                        }`}
                        title={f.sin_precio ? 'Sin tarifa vigente — cargala en C10' : undefined}
                      >
                        {f.sin_precio ? 'sin tarifa' : fmtCop(f.precio_unitario)}
                      </td>
                      <td
                        className="px-3 py-1.5 text-right tabular-nums"
                        title="Stickers × tarifa + termos × tarifa — se cobran adicional"
                      >
                        {f.ingreso_insumos > 0 ? fmtCop(f.ingreso_insumos) : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                        {fmtCop(f.valor_total)}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TablaFacturacion({ filas, qs }: { filas: FacturacionQFila[]; qs: FacturacionQQ[] }) {
  if (filas.length === 0) {
    return (
      <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
        No hay OPs terminadas en el período — la facturación sale de las OPs Terminadas del Cruce.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 bg-surface-overlay text-xs text-fg-tertiary">
          <tr>
            <th className="px-3 py-2 text-left">Cierre</th>
            <th className="px-3 py-2 text-left">Orden</th>
            <th className="px-3 py-2 text-left">SKU</th>
            <th className="px-3 py-2 text-left">Área</th>
            <th className="px-3 py-2 text-right">Programadas</th>
            <th className="px-3 py-2 text-right">Validadas ✓</th>
            <th className="px-3 py-2 text-right">Cumpl.</th>
            <th className="px-3 py-2 text-right">Tarifa u.</th>
            <th className="px-3 py-2 text-center">Dominical</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {qs.map((q) => {
            const filasQ = filas.filter((f) => f.q_key === q.q_key);
            return (
              <Fragment key={q.q_key}>
                <tr className="border-t-2 border-border-strong bg-surface-overlay/60 font-medium">
                  <td className="px-3 py-1.5 text-fg-secondary" colSpan={4}>
                    {tituloQ(q)}
                  </td>
                  <td className="px-3 py-1.5" />
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmt(q.unidades_validadas)}
                  </td>
                  <td className="px-3 py-1.5" colSpan={2} />
                  <td className="px-3 py-1.5 text-right tabular-nums text-fg-tertiary">
                    {q.valor_dominical > 0 ? fmtCop(q.valor_dominical) : ''}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtCop(q.valor_total)}</td>
                </tr>
                {filasQ.map((f) => (
                  <tr
                    key={`${f.numero_op}-${f.sku_id}-${f.planta_id}`}
                    className="border-t border-border/60"
                  >
                    <td
                      className="px-3 py-1.5 whitespace-nowrap text-fg-secondary"
                      title={
                        f.cerrada_manual
                          ? `Cierre manual en el cruce · producción ${fmtDia(f.fecha_inicio)} → ${fmtDia(f.fecha_fin)}`
                          : `Terminada automática (última producción validada) · ${fmtDia(f.fecha_inicio)} → ${fmtDia(f.fecha_fin)}`
                      }
                    >
                      {fmtDia(f.fecha_facturacion)}
                      {f.cerrada_manual ? '' : ' ·auto'}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums">{f.numero_op}</td>
                    <td className="max-w-64 truncate px-3 py-1.5" title={f.sku_descripcion}>
                      <span className="tabular-nums text-fg-secondary">{f.sku_codigo_cliente}</span>{' '}
                      {f.sku_descripcion}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-fg-secondary">
                      {f.area_nombre}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmt(f.cantidad_programada)}
                    </td>
                    <td
                      className="px-3 py-1.5 text-right tabular-nums"
                      title={
                        f.cantidad_validada_domingo > 0
                          ? `${fmt(f.cantidad_validada_domingo)} unidades en domingo`
                          : undefined
                      }
                    >
                      {fmt(f.cantidad_validada)}
                      {f.cantidad_validada_domingo > 0 ? (
                        <span className="ml-1 text-xs text-fg-tertiary">
                          ({fmt(f.cantidad_validada_domingo)} dom)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fmtPct(f.cumplimiento_pct)}
                    </td>
                    <td
                      className={`px-3 py-1.5 text-right tabular-nums ${
                        f.sin_precio ? 'text-warn' : ''
                      }`}
                      title={f.sin_precio ? 'Sin tarifa vigente — cargala en C10' : undefined}
                    >
                      {f.sin_precio ? 'sin tarifa' : fmtCop(f.precio_unitario)}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {f.cantidad_validada_domingo > 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                          <DominicalSwitch fila={f} />
                          {f.dominical_aplica ? (
                            <span className="text-xs tabular-nums text-fg-tertiary">×1.8</span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-xs text-fg-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                      {fmtCop(f.valor_total)}
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
